import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { z } from "zod";
import { Info, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizeAndCorrectEmail } from "@/lib/emailValidation";
import { handleMembershipRevoked } from "@/lib/authUtils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const signupSchema = z.object({
  email: z.string().email({ message: "Email invalide" }),
  password: z.string().min(6, { message: "Le mot de passe doit contenir au moins 6 caractères" }),
  firstName: z.string().min(1, { message: "Le prénom est requis" }).max(100),
  lastName: z.string().min(1, { message: "Le nom est requis" }).max(100),
  organizationId: z.string().min(1, { message: "Vous devez sélectionner une organisation" }),
});

interface Organization {
  id: string;
  name: string;
  code: string;
}

const loginSchema = z.object({
  email: z.string().email({ message: "Email invalide" }),
  password: z.string().min(1, { message: "Le mot de passe est requis" }),
});

const forgotPasswordSchema = z.object({
  email: z.string().email({ message: "Email invalide" }),
});

const Auth = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [openOrgCombobox, setOpenOrgCombobox] = useState(false);
  const [openForgotPasswordDialog, setOpenForgotPasswordDialog] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Get organization data from context
  const { user, membership, loading: orgLoading, isRefreshing } = useOrganization();

  useEffect(() => {
    // Load all organizations for the combobox
    const fetchOrganizations = async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, code")
        .order("name");

      if (!error && data) {
        setOrganizations(data);
      }
    };

    fetchOrganizations();
  }, []);

  // Navigate after successful login/signup
  useEffect(() => {
    console.log('[Auth] useEffect navigation - orgLoading:', orgLoading, 'isRefreshing:', isRefreshing, 'user:', !!user, 'membership:', !!membership, 'isAuthenticating:', isAuthenticating);

    // Wait for organization context to finish loading (initial load OR refresh from cross-tab broadcast)
    if (orgLoading || isRefreshing) {
      console.log('[Auth] Still loading org context (orgLoading:', orgLoading, 'isRefreshing:', isRefreshing, '), waiting...');
      return;
    }

    // If user is authenticated, navigate to appropriate page
    if (user) {
      if (membership) {
        console.log('[Auth] User has membership, navigating to dashboard');
        // User has membership, go to dashboard
        navigate("/", { replace: true });
      } else if (!isAuthenticating) {
        console.log('[Auth] User has NO membership AND isAuthenticating is FALSE -> calling handleMembershipRevoked');
        // User has no membership AND we're not in the middle of authenticating
        // This means membership was truly revoked (not just loading after login/signup)
        handleMembershipRevoked(navigate, toast);
      } else {
        console.log('[Auth] User has no membership BUT isAuthenticating is TRUE -> waiting for context refresh');
      }
      // Si isAuthenticating est true et membership est null, on attend...
      // Le contexte va refetch les données via onAuthStateChange
    }
  }, [user, membership, orgLoading, isRefreshing, isAuthenticating, navigate, toast]);

  // Reset isAuthenticating and loading when membership is loaded after login/signup
  useEffect(() => {
    console.log('[Auth] useEffect reset - membership:', !!membership, 'isAuthenticating:', isAuthenticating);
    if (membership && isAuthenticating) {
      console.log('[Auth] Membership loaded while isAuthenticating=true, resetting flags');
      setIsAuthenticating(false);
      setLoading(false);
    }
  }, [membership, isAuthenticating]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validatedData = signupSchema.parse({
        email,
        password,
        firstName,
        lastName,
        organizationId: selectedOrgId
      });

      // 1. Normaliser l'email
      const { correctedEmail, wasCorrected, correctionMessage } = normalizeAndCorrectEmail(validatedData.email);

      // Afficher un avertissement si l'email a été corrigé
      if (wasCorrected && correctionMessage) {
        toast({
          title: "Email corrigé",
          description: correctionMessage,
        });
      }

      // 2. Vérifier l'autorisation AVANT de créer le compte Supabase Auth
      const { data: authCheck, error: authCheckError } = await supabase.rpc(
        'check_signup_authorization',
        {
          p_organization_id: validatedData.organizationId,
          p_email: correctedEmail
        }
      );

      if (authCheckError) {
        toast({
          title: "Erreur de vérification",
          description: "Impossible de vérifier votre autorisation. Veuillez réessayer.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // 3. Si non autorisé, bloquer l'inscription
      if (!authCheck.authorized) {
        toast({
          title: "Email non autorisé",
          description: "Votre adresse email n'est pas autorisée à rejoindre cette organisation. Veuillez contacter un administrateur pour être ajouté à la liste des utilisateurs autorisés.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // 4. Si autorisé, créer le compte avec le rôle approprié
      // IMPORTANT: Activer isAuthenticating AVANT signUp car onAuthStateChange se déclenche immédiatement
      console.log('[Auth] Setting isAuthenticating=true BEFORE signUp');
      setIsAuthenticating(true);

      const { data: authData, error } = await supabase.auth.signUp({
        email: correctedEmail,
        password: validatedData.password,
        options: {
          data: {
            first_name: validatedData.firstName,
            last_name: validatedData.lastName,
          },
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        console.log('[Auth] signUp error, resetting isAuthenticating');
        setIsAuthenticating(false);
        if (error.message.includes("already registered")) {
          toast({
            title: "Compte existant",
            description: "Cet email est déjà enregistré. Veuillez vous connecter.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erreur d'inscription",
            description: error.message,
            variant: "destructive",
          });
        }
      } else if (authData.user) {
        // 5. Rejoindre l'organisation via la fonction serveur autoritative.
        //    Le rôle est calculé et borné côté serveur (jamais fourni par le client),
        //    l'email est lu depuis le JWT et l'invitation est consommée dans la même
        //    transaction. Le pré-check ci-dessus (check_signup_authorization) reste
        //    purement indicatif pour l'UX.
        const { data: joinResult, error: joinError } = await supabase.rpc(
          'join_organization_with_invite',
          { p_organization_id: validatedData.organizationId }
        );

        if (joinError || !joinResult?.authorized) {
          console.log('[Auth] joinError, resetting isAuthenticating', joinError, joinResult);
          setIsAuthenticating(false);
          toast({
            title: "Avertissement",
            description: "Compte créé mais impossible de rejoindre l'organisation. Veuillez contacter un administrateur.",
            variant: "destructive",
          });
        } else {
          const roleMessage = joinResult.role === 'admin'
            ? "Vous êtes le premier membre et avez été nommé administrateur."
            : "Vous avez été ajouté comme membre.";

          toast({
            title: "Inscription réussie !",
            description: roleMessage,
          });
          // Auto-login : isAuthenticating est déjà true (activé avant signUp)
          // Le contexte va recharger les données et rediriger vers /
          console.log('[Auth] Signup fully complete, isAuthenticating already true, returning');
          return; // Éviter le finally qui remet loading à false
        }
      }
    } catch (error) {
      console.log('[Auth] catch block, resetting isAuthenticating');
      setIsAuthenticating(false);
      if (error instanceof z.ZodError) {
        toast({
          title: "Erreur de validation",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const validatedData = loginSchema.parse({ email, password });

      // IMPORTANT: Activer isAuthenticating AVANT signInWithPassword car onAuthStateChange se déclenche immédiatement
      // Sans cela, le contexte peut voir user=set + membership=null + isAuthenticating=false
      // et appeler handleMembershipRevoked avant que les données ne soient chargées
      console.log('[Auth] Setting isAuthenticating=true BEFORE signInWithPassword');
      setIsAuthenticating(true);

      const { error } = await supabase.auth.signInWithPassword({
        email: validatedData.email,
        password: validatedData.password,
      });

      if (error) {
        console.log('[Auth] signInWithPassword error, resetting isAuthenticating');
        setIsAuthenticating(false);
        toast({
          title: "Erreur de connexion",
          description: "Email ou mot de passe incorrect.",
          variant: "destructive",
        });
        setLoading(false);
      }
      // Login réussi : garder loading=true et isAuthenticating=true
      // Le bouton reste "Connexion..." jusqu'à la redirection
    } catch (error) {
      console.log('[Auth] catch block, resetting isAuthenticating');
      setIsAuthenticating(false);
      if (error instanceof z.ZodError) {
        toast({
          title: "Erreur de validation",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    try {
      const validatedData = forgotPasswordSchema.parse({ email: forgotPasswordEmail });

      const { error } = await supabase.auth.resetPasswordForEmail(validatedData.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          title: "Erreur",
          description: "Une erreur s'est produite lors de l'envoi de l'email.",
          variant: "destructive",
        });
      } else {
        setOpenForgotPasswordDialog(false);
        setForgotPasswordEmail("");
        navigate("/password-reset-email-sent");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Erreur de validation",
          description: error.errors[0].message,
          variant: "destructive",
        });
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-background via-secondary/20 to-accent/10">
      {/* Bannière professionnels */}
      <div className="w-full bg-gradient-to-r from-orange-500 to-amber-600 py-3 px-4 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-2 text-primary-foreground">
          <Info className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm font-medium text-center">
            Application réservée aux professionnels Terre adélice
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
        {/* Branding Section */}
        <div className="text-center space-y-3">
          <img
            src="/logo-v-b.3-t.png"
            alt="Terre adélice"
            className="h-24 mx-auto object-contain"
          />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-fade-in">
            Fidélice
          </h1>
          <p className="text-base font-medium text-foreground/90">
            Carte de fidélité digitale Terre adélice
          </p>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto leading-relaxed">
            Ce service vous est offert en tant que licencié par Terre adélice
          </p>
        </div>

        {/* Auth Card */}
        <Card className="w-full shadow-[var(--shadow-medium)]">
          <CardHeader className="space-y-0.5 pb-4">
            <CardTitle className="text-xl font-bold text-center">
              Accéder à votre espace
            </CardTitle>
            <CardDescription className="text-center text-sm">
              Connectez-vous ou créez votre compte
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 bg-muted/30 p-1.5 rounded-xl h-auto">
                <TabsTrigger 
                  value="login" 
                  className="text-sm py-2.5 rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm font-semibold transition-all"
                >
                  Connexion
                </TabsTrigger>
                <TabsTrigger 
                  value="signup" 
                  className="text-sm py-2.5 rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm font-semibold transition-all"
                >
                  Inscription
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="login-email" className="text-sm font-semibold">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="br@terre-adelice.eu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 text-base"
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="login-password" className="text-sm font-semibold">Mot de passe</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 text-base"
                    />
                  </div>

                  <Button type="submit" className="w-full h-11 text-base font-semibold mt-4" disabled={loading}>
                    {loading ? "Connexion..." : "Se connecter"}
                  </Button>

                  <div className="text-center mt-3">
                    <button
                      type="button"
                      onClick={() => setOpenForgotPasswordDialog(true)}
                      className="text-sm text-primary hover:underline font-medium"
                      disabled={loading}
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="firstName" className="text-sm font-semibold">Prénom</Label>
                      <Input
                        id="firstName"
                        type="text"
                        placeholder="Bertrand"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                        disabled={loading}
                        className="h-11 text-base"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="lastName" className="text-sm font-semibold">Nom</Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Rousselle"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                        disabled={loading}
                        className="h-11 text-base"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="signup-email" className="text-sm font-semibold">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="br@terre-adelice.eu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 text-base"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-sm font-semibold">Organisation</Label>
                    <Popover open={openOrgCombobox} onOpenChange={setOpenOrgCombobox}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openOrgCombobox}
                          className="w-full h-11 justify-between text-base"
                          disabled={loading}
                        >
                          {selectedOrgId
                            ? organizations.find((org) => org.id === selectedOrgId)?.name
                            : "Sélectionnez votre organisation"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Rechercher une organisation..." />
                          <CommandList>
                            <CommandEmpty>Aucune organisation trouvée.</CommandEmpty>
                            <CommandGroup>
                              {organizations.map((org) => (
                                <CommandItem
                                  key={org.id}
                                  value={`${org.name} ${org.code}`}
                                  onSelect={() => {
                                    setSelectedOrgId(org.id);
                                    setOpenOrgCombobox(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedOrgId === org.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{org.name}</span>
                                    <span className="text-xs text-muted-foreground">Code: {org.code}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="signup-password" className="text-sm font-semibold">Mot de passe</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      className="h-11 text-base"
                    />
                  </div>

                  <Button type="submit" className="w-full h-11 text-base font-semibold mt-4" disabled={loading}>
                    {loading ? "Création du compte..." : "Créer mon compte"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Forgot Password Dialog */}
        <Dialog open={openForgotPasswordDialog} onOpenChange={setOpenForgotPasswordDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Mot de passe oublié</DialogTitle>
              <DialogDescription className="text-sm">
                Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleForgotPassword} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="forgot-password-email" className="text-sm font-semibold">
                  Email
                </Label>
                <Input
                  id="forgot-password-email"
                  type="email"
                  placeholder="br@terre-adelice.eu"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  disabled={forgotPasswordLoading}
                  className="h-11 text-base"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpenForgotPasswordDialog(false)}
                  disabled={forgotPasswordLoading}
                  className="flex-1 h-11 text-base"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="flex-1 h-11 text-base font-semibold"
                >
                  {forgotPasswordLoading ? "Envoi..." : "Envoyer le lien"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Auth;
